import { useEffect, useState } from 'react'
import type { RankingOut } from '@/api/endpoints/ranking'
import { Chip } from '@/components/ui/Chip'
import { Tooltip } from '@/components/ui/Tooltip'
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from '@/components/ui/Tabs'
import { cn } from '@/lib/cn'
import {
  GUARDRAIL_LABELS,
  HEURISTIC_LABELS,
  JUDGE_DIMENSIONS,
  TAB_LABELS,
  type RankingBreakdownTab,
} from './labels'
import styles from './RankingBreakdownView.module.scss'

export type { RankingBreakdownTab } from './labels'

export interface RankingBreakdownViewProps {
  ranking: RankingOut
  initialTab?: RankingBreakdownTab
  /** Renders inline (no negative-margin tab strip). Default ``false`` (inside Dialog body). */
  inline?: boolean
  /** Called whenever the user switches tabs — useful when the parent wants
   *  to mirror the active tab back into URL state. Optional. */
  onTabChange?: (tab: RankingBreakdownTab) => void
}

/**
 * Read-only detail of a ranking row's breakdown across the four contributors:
 * Group A guardrails, Group B emergent heuristics, LLM judge dimensions,
 * and the performance multiplier. Reused by:
 *   - the project-page RankingDock as a modal, and
 *   - the RankingPage row expand inline.
 *
 * No data fetching here — everything renders off the ``ranking`` prop, which
 * the caller already has cached via ``useRanking`` / ``useRankings``.
 */
export function RankingBreakdownView({
  ranking,
  initialTab = 'group_a',
  inline = false,
  onTabChange,
}: RankingBreakdownViewProps) {
  const [tab, setTab] = useState<RankingBreakdownTab>(initialTab)

  // When the caller swaps initialTab (e.g. user clicks a different metric
  // card before closing the modal), follow it.
  useEffect(() => {
    setTab(initialTab)
  }, [initialTab])

  const handleTabChange = (next: string) => {
    const t = next as RankingBreakdownTab
    setTab(t)
    onTabChange?.(t)
  }

  return (
    <div className={styles.shell}>
      <TabsRoot value={tab} onValueChange={handleTabChange}>
        <TabsList className={cn(styles.tabs, inline && styles.inlineTabs)} reorderKey="ranking-breakdown">
          {(Object.keys(TAB_LABELS) as RankingBreakdownTab[]).map((key) => (
            <TabsTrigger key={key} value={key}>
              {TAB_LABELS[key]}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="group_a">
          <GroupAPanel ranking={ranking} />
        </TabsContent>
        <TabsContent value="group_b">
          <GroupBPanel ranking={ranking} />
        </TabsContent>
        <TabsContent value="judge">
          <JudgePanel ranking={ranking} />
        </TabsContent>
        <TabsContent value="performance">
          <PerformancePanel ranking={ranking} />
        </TabsContent>
      </TabsRoot>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Group A — Guardrails
// ---------------------------------------------------------------------------

function GroupAPanel({ ranking }: { ranking: RankingOut }) {
  const violated = new Set(ranking.guardrails_violated)
  // Show every known guardrail rule (so users learn what's checked even on
  // a clean run); append unknown violations at the bottom for forward-compat.
  const knownKeys = Object.keys(GUARDRAIL_LABELS)
  const unknownKeys = [...violated].filter((k) => !(k in GUARDRAIL_LABELS))
  const allKeys = [...knownKeys, ...unknownKeys]

  const summaryGood = violated.size === 0
  return (
    <div className={styles.tabContent}>
      <div className={styles.summary}>
        <div>
          <div className={styles.summaryLabel}>Status</div>
          <div
            className={cn(
              styles.summaryValue,
              summaryGood ? styles.summaryValueGood : styles.summaryValueBad,
            )}
          >
            {summaryGood
              ? 'Limpo'
              : `${String(violated.size)} violação${violated.size === 1 ? '' : 'ões'}`}
          </div>
        </div>
        <div className={styles.summaryHint}>
          {summaryGood
            ? 'Todos os guardrails passaram. Score não foi capado.'
            : 'Final score capado em 0.20 enquanto houver violação.'}
        </div>
      </div>

      <div className={cn(styles.table, styles.guardrailsTable)}>
        <div className={styles.tableHead}>
          <span>Regra</span>
          <span style={{ textAlign: 'right' }}>Status</span>
        </div>
        {allKeys.map((key) => {
          const known = GUARDRAIL_LABELS[key]
          const failed = violated.has(key)
          return (
            <div className={styles.row} key={key}>
              <div className={styles.label}>
                <span className={styles.labelTitle}>{known?.label ?? key}</span>
                {known ? <span className={styles.labelHelp}>{known.help}</span> : null}
              </div>
              <span
                className={failed ? styles.statusFail : styles.statusPass}
                style={{ textAlign: 'right' }}
              >
                {failed ? '✗ falhou' : '✓ passou'}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Group B — Heuristics
// ---------------------------------------------------------------------------

function GroupBPanel({ ranking }: { ranking: RankingOut }) {
  const breakdown = ranking.breakdown as RankingBreakdownDict
  const heuristic = breakdown.heuristic
  const signals = heuristic?.signals ?? {}
  const weights = breakdown._weights?.heuristic ?? {}

  const knownKeys = Object.keys(HEURISTIC_LABELS)
  const unknownKeys = Object.keys(signals).filter((k) => !(k in HEURISTIC_LABELS))
  const rows = [...knownKeys, ...unknownKeys].filter((k) => k in signals || k in weights)

  if (rows.length === 0) {
    return (
      <div className={styles.tabContent}>
        <div className={styles.summary}>
          <div>
            <div className={styles.summaryLabel}>Score Grupo B</div>
            <div className={styles.summaryValue}>{formatScore(ranking.heuristic_b_score)}</div>
          </div>
          <div className={styles.summaryHint}>Sinais detalhados não disponíveis para esta versão de critério.</div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.tabContent}>
      <div className={styles.summary}>
        <div>
          <div className={styles.summaryLabel}>Score Grupo B</div>
          <div className={styles.summaryValue}>{formatScore(ranking.heuristic_b_score)}</div>
        </div>
        <div className={styles.summaryHint}>
          Sinais emergentes que o generator não controla diretamente. Pesos vêm da
          versão {ranking.criteria_version} dos critérios.
        </div>
      </div>

      <div className={cn(styles.table, styles.heuristicsTable)}>
        <div className={styles.tableHead}>
          <span>Heurística</span>
          <span style={{ textAlign: 'right' }}>Valor</span>
          <span style={{ textAlign: 'right' }}>Peso</span>
          <span style={{ textAlign: 'right' }}>Contribuição</span>
        </div>
        {rows.map((key) => {
          const known = HEURISTIC_LABELS[key]
          const value = signals[key]
          const weight = weights[key]
          const contribution =
            typeof value === 'number' && typeof weight === 'number' ? value * weight : null
          return (
            <div className={styles.row} key={key}>
              <div className={styles.label}>
                <span className={styles.labelTitle}>{known?.label ?? key}</span>
                {known ? <span className={styles.labelHelp}>{known.help}</span> : null}
              </div>
              <span className={styles.numericCell}>{formatNumber(value, 3)}</span>
              <span className={cn(styles.numericCell, styles['numericCell--muted'])}>
                {formatNumber(weight, 2)}
              </span>
              <span className={styles.numericCell}>{formatNumber(contribution, 3)}</span>
            </div>
          )
        })}
        <div className={cn(styles.row, styles.totalRow)}>
          <span className={styles.labelTitle}>Total Grupo B</span>
          <span className={styles.numericCell}>—</span>
          <span className={styles.numericCell}>—</span>
          <span className={styles.numericCell}>{formatScore(ranking.heuristic_b_score)}</span>
        </div>
      </div>

      {Array.isArray(breakdown.failed_components) && breakdown.failed_components.length > 0 ? (
        <div className={styles.failedComponents}>
          {breakdown.failed_components.map((c) => (
            <Tooltip key={c} content="Componente caiu para fallback neutro durante o scoring.">
              <Chip variant="warning">{c}</Chip>
            </Tooltip>
          ))}
        </div>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Judge — modelo de nuvem configurado
// ---------------------------------------------------------------------------

function JudgePanel({ ranking }: { ranking: RankingOut }) {
  const breakdown = ranking.breakdown as RankingBreakdownDict
  const judge = breakdown.judge
  const judgeWeights = breakdown._weights?.judge ?? {}

  if (!judge) {
    return (
      <div className={styles.tabContent}>
        <div className={styles.empty}>
          {ranking.judge_score === null
            ? 'Juiz indisponível para este vídeo. O ranking foi calculado sem a contribuição do juiz (warmup, falha do juiz ou observacional).'
            : 'Sem detalhamento granular do juiz nesta linha — apenas o score agregado está disponível.'}
        </div>
      </div>
    )
  }

  return (
    <div className={styles.tabContent}>
      <div className={styles.summary}>
        <div>
          <div className={styles.summaryLabel}>Score do juiz</div>
          <div className={styles.summaryValue}>{formatScore(ranking.judge_score)}</div>
        </div>
        <div className={styles.summaryHint}>
          Avalia dimensões que o generator NÃO controla. Anti-tautológico.
        </div>
      </div>

      <div className={cn(styles.table, styles.judgeTable)}>
        <div className={styles.tableHead}>
          <span>Dimensão (0–10)</span>
          <span style={{ textAlign: 'right' }}>Nota</span>
          <span style={{ textAlign: 'right' }}>Peso</span>
          <span style={{ textAlign: 'right' }}>Contribuição</span>
        </div>
        {JUDGE_DIMENSIONS.map((dim) => {
          const raw = (judge as Record<string, unknown>)[dim.key]
          const value = typeof raw === 'number' ? raw : null
          const weight = judgeWeights[dim.key]
          // For inverse dims, the score formula uses (1 - x/10); contribution
          // here mirrors that so the table sums roughly to the judge score.
          const contribution =
            typeof value === 'number' && typeof weight === 'number'
              ? (dim.inverse ? 1 - value / 10 : value / 10) * weight
              : null
          return (
            <div className={styles.row} key={dim.key}>
              <div className={styles.label}>
                <span
                  className={cn(styles.labelTitle, dim.inverse && styles.labelTitleInverse)}
                >
                  {dim.label}
                  {dim.inverse ? ' (inversa)' : ''}
                </span>
                <span className={styles.labelHelp}>{dim.help}</span>
              </div>
              <span className={styles.numericCell}>{formatNumber(value, 1)}</span>
              <span className={cn(styles.numericCell, styles['numericCell--muted'])}>
                {formatNumber(weight, 2)}
              </span>
              <span className={styles.numericCell}>{formatNumber(contribution, 3)}</span>
            </div>
          )
        })}
      </div>

      <div className={styles.judgeMeta}>
        <span>
          <strong>Drop-off estimado:</strong>{' '}
          {typeof judge.drop_off_second === 'number' ? `${String(judge.drop_off_second)}s` : '—'}
        </span>
      </div>

      {judge.explanation ? <div className={styles.judgeText}>{judge.explanation}</div> : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Performance multiplier
// ---------------------------------------------------------------------------

function PerformancePanel({ ranking }: { ranking: RankingOut }) {
  const breakdown = ranking.breakdown as RankingBreakdownDict
  const perf = breakdown.performance

  const multiplier = ranking.perf_multiplier
  const variant: 'good' | 'bad' | 'neutral' =
    multiplier > 1 ? 'good' : multiplier < 1 ? 'bad' : 'neutral'

  return (
    <div className={styles.tabContent}>
      <div className={styles.summary}>
        <div>
          <div className={styles.summaryLabel}>Multiplicador</div>
          <div
            className={cn(
              styles.summaryValue,
              variant === 'good' && styles.summaryValueGood,
              variant === 'bad' && styles.summaryValueBad,
            )}
          >
            {`${multiplier.toFixed(2)}×`}
          </div>
        </div>
        <div className={styles.summaryHint}>
          {variant === 'good'
            ? `Boost de ${formatPercent(multiplier - 1)} sobre o score base.`
            : variant === 'bad'
              ? `Penalidade de ${formatPercent(1 - multiplier)} sobre o score base.`
              : 'Multiplicador neutro — sem amostras suficientes ou cell na média.'}
        </div>
      </div>

      <div className={cn(styles.table, styles.performanceTable)}>
        <div className={styles.tableHead}>
          <span>Item</span>
          <span style={{ textAlign: 'right' }}>Valor</span>
        </div>
        <div className={styles.row}>
          <div className={styles.label}>
            <span className={styles.labelTitle}>Cell key</span>
            <span className={styles.labelHelp}>
              channel/lang/factory/pattern — agrupamento usado pra calcular a mediana.
            </span>
          </div>
          <span className={styles.numericCell}>{perf?.cell_key ?? '—'}</span>
        </div>
        <div className={styles.row}>
          <div className={styles.label}>
            <span className={styles.labelTitle}>Amostras na cell</span>
            <span className={styles.labelHelp}>
              Quantos vídeos da mesma cell entraram na estatística (mediana + MAD).
            </span>
          </div>
          <span className={styles.numericCell}>
            {typeof perf?.n_samples_in_cell === 'number' ? String(perf.n_samples_in_cell) : '—'}
          </span>
        </div>
        <div className={styles.row}>
          <div className={styles.label}>
            <span className={styles.labelTitle}>Multiplicador aplicado</span>
            <span className={styles.labelHelp}>
              Saturado em [0.5, 1.5]. 1.0 = neutro (insuficiente ou na mediana).
            </span>
          </div>
          <span className={styles.numericCell}>
            {typeof perf?.multiplier === 'number' ? `${perf.multiplier.toFixed(2)}×` : '—'}
          </span>
        </div>
      </div>

      {(perf?.n_samples_in_cell ?? 0) === 0 ? (
        <div className={styles.empty}>
          Sem amostras t30d nesta cell ainda — multiplicador neutro até o YouTube
          Analytics agregar dados suficientes.
        </div>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers — local types and formatters
// ---------------------------------------------------------------------------

interface RankingBreakdownDict {
  heuristic?: {
    signals?: Record<string, number>
    guardrails_violated?: string[]
    freshness_boost?: number
  }
  judge?: {
    emotional_resonance?: number
    surprise_factor?: number
    narrative_tension?: number
    predictability?: number
    authentic_voice?: number
    factual_risk?: number
    drop_off_second?: number | null
    explanation?: string
  } | null
  performance?: {
    multiplier?: number
    n_samples_in_cell?: number
    cell_key?: string
  }
  failed_components?: string[]
  _weights?: {
    heuristic?: Record<string, number>
    judge?: Record<string, number>
    w_heur?: number
    w_judge?: number
  }
}

function formatScore(score: number | null | undefined): string {
  if (score === null || score === undefined) return '—'
  return score.toFixed(2)
}

function formatNumber(n: number | null | undefined, digits = 2): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—'
  return n.toFixed(digits)
}

function formatPercent(p: number): string {
  return `${(p * 100).toFixed(0)}%`
}
